import {
  Grid,
  Heading,
  Box,
  Text,
  Icon,
  LinkBox,
  LinkOverlay,
  GridItem,
  Flex,
  Link,
  HStack,
  VStack,
  Stack,
  Button,
  Container,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FiZap,
  FiInfo,
  FiEdit3,
  FiCalendar,
} from 'react-icons/fi';
import MainLayout from '@src/components/layouts/main';
import { useGetContent } from '@src/modules/settings/context/settings-api-hooks';
import RoleGuard from '../shared/RoleGuard';
import Slider from '../shared/Slider';

function ImageCard({
  title, image, to, isExternal, imagePostion = 'center',
}) {
  return (
    <Box
      backgroundImage={`url(${image})`}
      backgroundSize="cover"
      backgroundPosition={imagePostion}
      borderRadius="md"
      boxShadow="md"
      border="3px solid"
      borderColor="white"
      textAlign="left"
      overflow="hidden"
      position="relative"
      transition="all 0.2s ease-in-out"
      minH={{ base: '100px', md: '130px', xl: '140px' }}
      _hover={{
        boxShadow: 'lg',
        borderColor: 'orange.400',
      }}
    >
      <LinkBox
        as="article"
        height="100%"
        display="block"
        _hover={{
          backdropFilter: 'blur(2px)',

        }}
      >
        <Flex
          px={4}
          py={3}
          height="100%"
          background="blackAlpha.600"
          color="white"
          flexDirection="column"
          justify="center"
          align="center"
        >
          <LinkOverlay
            as={isExternal ? Link : RouterLink}
            to={isExternal ? undefined : to}
            href={isExternal ? to : undefined}
            color="white"
            fontWeight="normal"
            textDecoration="none"
          >
            <Heading
              textAlign="center"
              as="h3"
              mb={0}
              fontSize={{ base: 'md', sm: 'lg', '2xl': '2xl' }}
            >
              {title}
            </Heading>
          </LinkOverlay>
        </Flex>
      </LinkBox>
    </Box>
  );
}

export default function HomePage() {
  const { data: events, isLoading: isLoadingEvents } = useGetContent('coming-soon');
  const { data: newFeatures, isLoading: isLoadingNewFeatures } = useGetContent('new-features');
  const { data: tipsAndTricks, isLoading: isLoadingTipsAndTricks } = useGetContent('tips-and-tricks');
  return (
    <MainLayout backgroundColor="gray.50" p={0} py={0}>
      <Box position="relative">
        <Box
          position="absolute"
          inset={0}
          backgroundImage="url('/images/home-bg.jpg')"
          backgroundSize="cover"
          backgroundPosition="50% 70%"
          zIndex={0}
        />
        <RoleGuard allowedRoles={['Developer']}>
          <Button
            as={RouterLink}
            to="/settings/cms/pages/home"
            position="absolute"
            top={6}
            right={6}
            zIndex={10}
            size="sm"
            colorScheme="blackAlpha"
            leftIcon={<Icon as={FiEdit3} boxSize={4} />}
          >
            Homepage Admin
          </Button>
        </RoleGuard>
        <Container maxW="full" position="relative" zIndex={1}>
          <Stack
            maxW="fit-content"
            my={10}
            spacing={4}
            align="flex-start"
            textAlign="left"
            backgroundColor="blackAlpha.500"
            backdropFilter="blur(6px)"
            p={6}
            borderRadius="md"
          >
            <Heading
              as="h1"
              fontSize={{ base: '2xl', md: '4xl', xl: '5xl' }}
              color="white"
              lineHeight={1}
              textShadow="md"
              mb={0}
            >
              Welcome to Transport Insights
            </Heading>
            <Heading
              fontWeight="normal"
              as="h2"
              fontSize={{ base: 'md', md: 'xl', lg: '2xl', '2xl': '3xl' }}
              color="whiteAlpha.800"
              textShadow="base"
              textAlign="left"
            >
              Supporting the sector by providing insights into
              <br />
              national, regional and local performance
            </Heading>
            <Stack
              spacing={3}
              justify="center"
              flexWrap="wrap"
              direction={{ base: 'column', md: 'row' }}
            >
              <Button
                as={Link}
                href="/Home/HelpLink/new-to"
                size="md"
                colorScheme="orange"
              >
                New to Transport Insights?
              </Button>
              <Button
                as={Link}
                href="/Home/HelpLink/basics"
                size="md"
                colorScheme="whiteAlpha"
                variant="outline"
                color="whiteAlpha.800"
              >
                Learn the Basics
              </Button>
            </Stack>
          </Stack>
          <Grid
            templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
            gap={4}
            pb={4}
          >
            <ImageCard
              title="Performance Dashboard"
              image="/images/performance-card.jpg"
              to="/performance/dashboard"
            />
            <ImageCard
              title="ONF Reporting"
              image="/images/onf-card.jpg"
              to="/onf/transport-outcomes"
            />
            <ImageCard
              title="Data Quality Dashboard"
              image="/images/data-quality-card.jpg"
              to="/DataQuality2"
              imagePostion="top"
              isExternal
            />
          </Grid>
        </Container>
      </Box>
      <Box
        display="flex"
        flex={{ base: 'none', lg: 1 }}
        minH={{ base: 'auto', lg: 0 }}
        backgroundColor="white"
        boxShadow="lg"
        py={4}
      >
        <Container maxW="full">
          <Grid
            templateColumns={{ base: '1fr', lg: '1fr 1fr 1fr' }}
            gap={4}
          >
            <GridItem display="flex">
              <Box
                h="100%"
                w="100%"
                border="2px solid"
                borderColor="gray.100"
                borderRadius="md"
                px={4}
              >
                <Slider
                  title="Coming Soon"
                  icon={FiCalendar}
                  loading={isLoadingEvents}
                >
                  {events && events.map((event) => (
                    <HStack
                      key={event.code}
                      align="flex-start"
                      spacing={4}
                    >
                      <Box
                        borderRadius="base"
                        overflow="hidden"
                        display="inline-block"
                        border="1px solid"
                        borderColor="gray.100"
                        textAlign="center"
                        minW="60px"
                      >
                        <Box
                          bg="orange.500"
                          px={3}
                          py={1}
                          textAlign="center"
                        >
                          <Text fontSize="xs" fontWeight="bold" color="white" letterSpacing="wide">
                            {new Date(event.date).toLocaleString('default', { month: 'short' }).toUpperCase()}
                          </Text>
                        </Box>
                        <VStack spacing={0} px={3} py={1} bg="white">
                          {event.dateFormat === 'DMY' && (
                          <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
                            {new Date(event.date).getDate()}
                          </Text>
                          )}
                          <Text fontSize="xs" color="gray.500">
                            {new Date(event.date).getFullYear()}
                          </Text>
                        </VStack>
                      </Box>
                      <VStack align="flex-start" spacing={1} pr={6}>
                        <Heading as="h4" size="sm">
                          {event.title}
                        </Heading>
                        <Box
                          fontSize="sm"
                          mt={2}
                          dangerouslySetInnerHTML={{ __html: event.descriptionHtml }}
                        />
                      </VStack>
                    </HStack>
                  ))}
                </Slider>
              </Box>
            </GridItem>
            <GridItem display="flex">
              <Box
                h="100%"
                w="100%"
                border="2px solid"
                borderColor="gray.100"
                borderRadius="md"
                px={4}
              >
                <Slider
                  title="Latest Updates"
                  icon={FiZap}
                  loading={isLoadingNewFeatures}
                >
                  {newFeatures && newFeatures.map((feature) => (
                    <Box
                      key={feature.code}
                      fontSize="sm"
                      px={6}
                      dangerouslySetInnerHTML={{
                        __html: feature?.message || 'No updates available',
                      }}
                    />
                  ))}
                </Slider>
              </Box>
            </GridItem>
            <GridItem display="flex">
              <Box
                h="100%"
                w="100%"
                border="2px solid"
                borderColor="gray.100"
                borderRadius="md"
                px={4}
              >
                <Slider
                  title="Did you know?"
                  icon={FiInfo}
                  loading={isLoadingTipsAndTricks}
                  autoAdvanceMs={5000}
                  pauseOnHover={false}
                >
                  {tipsAndTricks && tipsAndTricks.map((tip) => (
                    <Box
                      key={tip.code}
                      fontSize="sm"
                      px={6}
                      dangerouslySetInnerHTML={{
                        __html: tip?.message || 'No content available',
                      }}
                    />
                  ))}
                </Slider>
              </Box>
            </GridItem>
          </Grid>
        </Container>
      </Box>
    </MainLayout>
  );
}
